import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { teamApi, projectsApi, entitiesApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, XCircle, Ban, CheckCircle2, ListTodo } from "lucide-react";
import TeamInviteDialog from "@/components/TeamInviteDialog";

const PROJECT_ROLES = [
  "discovery_agent", "researcher", "research_coordinator",
  "business_development", "community_engagement", "pro_network_advisor",
  "project_coordinator", "project_manager", "producer", "co_producer",
  "host", "co_host", "content_communications", "operations",
  "advisor", "contributor", "observer",
];
const ACCESS_LEVELS = [
  { value: "view", label: "View" },
  { value: "contribute", label: "Contribute" },
  { value: "manage_project", label: "Manage Project" },
];
const roleLabel = (slug) => (slug || "").split("_").map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(" ");
const accessLabel = (slug) => (ACCESS_LEVELS.find(a => a.value === slug)?.label) || roleLabel(slug);
const ROLES = PROJECT_ROLES;  // retained — invite form uses it below

// A tombstoned user has had their email scrubbed on `DELETE /team/users/{uid}`.
// Hide every write affordance for them — Enable/Disable/Assign/Delete.
const isDeleted = (u) => !!u?.deleted_at || (u?.email || "").startsWith("deleted+");

function AssignedTasksBlock({ userId, open }) {
  const [tasks, setTasks] = useState(null);
  useEffect(() => {
    if (!open || tasks !== null) return;
    (async () => {
      try {
        const r = await teamApi.listUserTasks(userId);
        setTasks(r.data || []);
      } catch { setTasks([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!open) return null;
  if (tasks === null) {
    return <div className="mt-3 text-xs text-white/40" data-testid={`tasks-loading-${userId}`}>Loading tasks…</div>;
  }
  if (tasks.length === 0) {
    return <div className="mt-3 text-xs text-white/50 italic" data-testid={`tasks-empty-${userId}`}>No tasks assigned.</div>;
  }
  const colour = (p) => p === "Critical" ? "text-red-300" : p === "High" ? "text-amber-300" : p === "Medium" ? "text-sky-300" : "text-white/60";
  return (
    <div className="mt-3 rounded-md border border-white/10 bg-white/[0.02]" data-testid={`assigned-tasks-${userId}`}>
      <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-white/40 border-b border-white/5">Assigned tasks ({tasks.length})</div>
      <div className="divide-y divide-white/5">
        {tasks.map(t => (
          <div key={t.id} className="px-3 py-2 flex items-center justify-between gap-3 text-xs cursor-pointer hover:bg-white/5"
               data-testid={`assigned-task-${t.id}`}
               onClick={() => (window.location.href = `/task/${t.id}`)}>
            <div className="min-w-0 flex-1">
              <div className="text-white/90 truncate">{t.name}</div>
              <div className="text-white/50 text-[11px] mt-0.5 truncate">
                {t.project_name || "—"}{t.entity_name ? ` · ${t.entity_name}` : ""}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0 text-[11px]">
              <span className="text-white/60">{t.status || "—"}</span>
              <span className={colour(t.priority)}>{t.priority || "—"}</span>
              <span className="text-white/50">{t.due_date || "—"}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Two-step entity → project picker. Renders an entity <select> that
// filters the project <select> options. The underlying membership +
// permission model is untouched — we only submit `project_id` /
// `project_role` to the backend as before.
function EntityProjectPicker({ value, onChange, entities, projects, testidPrefix }) {
  const projById = useMemo(
    () => Object.fromEntries(projects.map(p => [p.id, p])),
    [projects]
  );
  const impliedEntityId = value ? projById[value]?.entity_id || "" : "";
  const [entityId, setEntityId] = useState(impliedEntityId);
  useEffect(() => { setEntityId(impliedEntityId); }, [impliedEntityId]);
  const filteredProjects = useMemo(() => {
    if (!entityId) return projects;
    return projects.filter(p => p.entity_id === entityId);
  }, [projects, entityId]);

  return (
    <div className="grid grid-cols-2 gap-2">
      <Select
        value={entityId || "__all"}
        onValueChange={v => {
          const next = v === "__all" ? "" : v;
          setEntityId(next);
          // Reset project when entity changes if project no longer fits.
          if (value && projById[value]?.entity_id !== next && next) {
            onChange("");
          }
        }}>
        <SelectTrigger className="mt-1.5 bg-white/5 border-white/10"
                       data-testid={`${testidPrefix}-entity`}>
          <SelectValue placeholder="All entities" />
        </SelectTrigger>
        <SelectContent className="bg-[#151A23] border-white/10 text-white">
          <SelectItem value="__all">All entities</SelectItem>
          {entities.map(e => (
            <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={value || "__none"}
        onValueChange={v => onChange(v === "__none" ? "" : v)}>
        <SelectTrigger className="mt-1.5 bg-white/5 border-white/10"
                       data-testid={`${testidPrefix}-project`}>
          <SelectValue placeholder={entityId ? "Choose project" : "— optional —"} />
        </SelectTrigger>
        <SelectContent className="bg-[#151A23] border-white/10 text-white max-h-80">
          <SelectItem value="__none">— no project —</SelectItem>
          {entityId ? (
            filteredProjects.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))
          ) : (
            entities.map(e => {
              const projs = projects.filter(p => p.entity_id === e.id);
              if (projs.length === 0) return null;
              return (
                <SelectGroup key={e.id}>
                  <SelectLabel className="text-white/50">{e.name}</SelectLabel>
                  {projs.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectGroup>
              );
            })
          )}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function TeamPage() {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [projects, setProjects] = useState([]);
  const [entities, setEntities] = useState([]);
  const [sending, setSending] = useState(false);
  const [assignFor, setAssignFor] = useState(null);
  const [tasksFor, setTasksFor] = useState(null);
  const [assignForm, setAssignForm] = useState({ project_id: "", project_role: "contributor", access_level: "contribute", custom_role: "" });
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = async () => {
    try {
      const [u, i, p, e] = await Promise.all([
        teamApi.listUsers(), teamApi.listInvitations(),
        projectsApi.list(), entitiesApi.list(),
      ]);
      setUsers(u.data || []); setInvites(i.data || []);
      setProjects(p.data || []); setEntities(e.data || []);
    } catch (e) { toast.error("Failed to load team"); }
  };
  useEffect(() => { load(); }, []);

  const submitAssign = async (uid) => {
    if (!assignForm.project_id) return toast.error("Choose a project");
    const role = assignForm.project_role === "__custom"
      ? (assignForm.custom_role || "").trim() || "contributor"
      : assignForm.project_role;
    try {
      await teamApi.addMembership({
        user_id: uid, project_id: assignForm.project_id,
        project_role: role, access_level: assignForm.access_level,
      });
      toast.success("Assigned"); setAssignFor(null);
      setAssignForm({ project_id: "", project_role: "contributor", access_level: "contribute", custom_role: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Assign failed"); }
  };
  const revoke = async (mid) => { await teamApi.removeMembership(mid); toast.success("Access revoked"); load(); };
  const disable = async (uid) => { if (!window.confirm("Disable this account? They will be unable to log in.")) return; await teamApi.disableUser(uid); toast.success("Account disabled"); load(); };
  const enable = async (uid) => { await teamApi.enableUser(uid); toast.success("Account enabled"); load(); };
  const removeUser = async (uid, email) => {
    const confirmed = window.confirm(`Permanently delete ${email}?\n\nThis cannot be undone. Their task, note, and audit history will be preserved under "Deleted User". The email can be invited again later.`);
    if (!confirmed) return;
    try {
      await teamApi.deleteUser(uid);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const revokeInv = async (id) => { await teamApi.revokeInvitation(id); toast.success("Invite revoked"); load(); };

  // Return "Entity → Project" so every project reference on the page
  // makes its owning entity obvious.
  const labelFor = (pid) => {
    const p = projects.find(pp => pp.id === pid);
    if (!p) return pid;
    const ent = entities.find(e => e.id === p.entity_id);
    return ent ? `${ent.name} → ${p.name}` : p.name;
  };
  const L = "text-white/60 text-xs uppercase tracking-wider";
  const I = "mt-1.5 bg-white/5 border-white/10";

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden max-w-full" data-testid="team-page">
      <div className="px-4 md:px-8 py-6 md:py-8 max-w-5xl mx-auto">
        <div className="mb-6">
          <div className="text-eyebrow">Executive Overview</div>
          <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight mt-1">Team</h1>
          <p className="text-sm text-white/60 mt-2 max-w-2xl">Invite team members and assign them to specific projects. Team members only see the projects they are assigned to.</p>
        </div>

        <div className="mb-8 p-4 rounded-xl border border-white/10 bg-[#151A23] flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-eyebrow">Add team member</div>
            <p className="text-xs text-white/50 mt-1">
              Search existing Contacts first — one person, one record across HQ.
            </p>
          </div>
          <Button onClick={() => setInviteOpen(true)}
                  data-testid="open-invite-dialog-btn"
                  className="rounded-full bg-white text-black hover:bg-white/90">
            <UserPlus className="w-4 h-4 mr-2" /> Add member
          </Button>
        </div>

        <div className="mb-8">
          <div className="text-eyebrow mb-2">Pending invitations</div>
          {invites.filter(i => i.status === "pending").length === 0 ? (
            <div className="text-sm text-white/40">No pending invites.</div>
          ) : (
            <div className="space-y-2">
              {invites.filter(i => i.status === "pending").map(i => (
                <div key={i.id} className="p-3 rounded-lg border border-white/10 bg-white/[0.03] flex items-center justify-between flex-wrap gap-2" data-testid={`invite-row-${i.id}`}>
                  <div>
                    <div className="text-white">{i.email}</div>
                    <div className="text-xs text-white/50">{i.platform_role} · expires {i.expires_at?.slice(0,10)}{i.project_id ? ` · ${labelFor(i.project_id)} (${i.project_role || "contributor"})` : ""}</div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => revokeInv(i.id)} className="text-red-400 hover:bg-red-500/10"><XCircle className="w-4 h-4 mr-1"/>Revoke</Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="text-eyebrow mb-2">Members</div>
          {users.length === 0 && <div className="text-sm text-white/40">No members yet.</div>}
          {users.map(u => (
            <div key={u.id} className="p-4 rounded-lg border border-white/10 bg-white/[0.03] mb-2" data-testid={`user-row-${u.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-white font-medium">
                    {isDeleted(u) ? (
                      <span>{u.name}</span>
                    ) : (
                      <Link to={`/member/${u.id}`}
                            className="hover:underline"
                            data-testid={`member-link-${u.id}`}>
                        {u.name}
                      </Link>
                    )}
                    <span className="text-xs text-white/50"> · {u.email}</span>
                    {isDeleted(u) && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-white/15 bg-white/5 text-white/60">
                        Deleted
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-white/50">{u.platform_role || u.role} {u.disabled && !isDeleted(u) ? "· disabled" : ""}</div>
                </div>
                {isDeleted(u) ? (
                  <span className="text-xs text-white/40 italic">Historical only — no actions available</span>
                ) : (
                  <div className="flex gap-1">
                    {u.disabled ? (
                      <Button size="sm" variant="ghost" onClick={() => enable(u.id)} className="text-emerald-300 hover:bg-emerald-500/10"><CheckCircle2 className="w-4 h-4 mr-1"/>Enable</Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => disable(u.id)} className="text-red-400 hover:bg-red-500/10"><Ban className="w-4 h-4 mr-1"/>Disable</Button>
                    )}
                    <Button size="sm" variant="ghost"
                            onClick={() => setTasksFor(tasksFor === u.id ? null : u.id)}
                            className="text-white/80 hover:bg-white/10"
                            data-testid={`tasks-btn-${u.id}`}>
                      <ListTodo className="w-4 h-4 mr-1"/>{tasksFor === u.id ? "Hide tasks" : "Show tasks"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setAssignFor(u.id)} className="text-white/80 hover:bg-white/10" data-testid={`assign-btn-${u.id}`}><UserPlus className="w-4 h-4 mr-1"/>Assign to project</Button>
                    {(u.platform_role || u.role) !== "admin" && (
                      <Button size="sm" variant="ghost"
                              onClick={() => removeUser(u.id, u.email)}
                              className="text-red-400 hover:bg-red-500/10"
                              data-testid={`delete-user-${u.id}`}>
                        <XCircle className="w-4 h-4 mr-1"/>Delete
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {u.memberships?.length > 0 && (
                <div className="mt-2 text-xs text-white/70 space-y-1" data-testid={`memberships-${u.id}`}>
                  {u.memberships.map(m => {
                    const proj = projects.find(pp => pp.id === m.project_id);
                    const ent = proj ? entities.find(e => e.id === proj.entity_id) : null;
                    return (
                      <div key={m.id} className="flex items-start justify-between gap-2 py-1">
                        <div>
                          <div className="text-white/85">
                            {ent?.name || "—"} <span className="text-white/40">→</span> {proj?.name || m.project_id}
                          </div>
                          <div className="text-[11px] text-white/50 mt-0.5">
                            Role: <span className="text-white/80">{roleLabel(m.project_role) || "—"}</span>
                            {" · "}Access: <span className="text-white/80">{accessLabel(m.access_level) || "Contribute"}</span>
                            {!m.active && <span className="text-red-300"> · revoked</span>}
                          </div>
                        </div>
                        {m.active && !isDeleted(u) && (
                          <button onClick={() => revoke(m.id)}
                                  className="text-red-400 hover:underline text-[11px]"
                                  data-testid={`revoke-membership-${m.id}`}>
                            Revoke access
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {!isDeleted(u) && (
                <AssignedTasksBlock userId={u.id} open={tasksFor === u.id} />
              )}
              {assignFor === u.id && (
                <div className="mt-3 p-3 rounded border border-white/10 bg-white/[0.02] space-y-2">
                  <EntityProjectPicker
                    value={assignForm.project_id}
                    onChange={pid => setAssignForm({ ...assignForm, project_id: pid })}
                    entities={entities}
                    projects={projects}
                    testidPrefix={`assign-${u.id}`} />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className={L}>Project role</Label>
                      <Select value={assignForm.project_role} onValueChange={v=>setAssignForm({...assignForm, project_role: v})}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-sm mt-1" data-testid={`assign-role-${u.id}`}><SelectValue/></SelectTrigger>
                        <SelectContent className="bg-[#151A23] border-white/10 text-white max-h-80">
                          {PROJECT_ROLES.map(r => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}
                          <SelectItem value="__custom">Custom role…</SelectItem>
                        </SelectContent>
                      </Select>
                      {assignForm.project_role === "__custom" && (
                        <Input value={assignForm.custom_role}
                               onChange={e => setAssignForm({...assignForm, custom_role: e.target.value})}
                               placeholder="Custom role name"
                               className="mt-1.5 bg-white/5 border-white/10 text-sm"
                               data-testid={`assign-custom-role-${u.id}`} />
                      )}
                    </div>
                    <div>
                      <Label className={L}>Access level</Label>
                      <Select value={assignForm.access_level} onValueChange={v=>setAssignForm({...assignForm, access_level: v})}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-sm mt-1" data-testid={`assign-access-${u.id}`}><SelectValue/></SelectTrigger>
                        <SelectContent className="bg-[#151A23] border-white/10 text-white">
                          {ACCESS_LEVELS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => submitAssign(u.id)} className="bg-white text-black hover:bg-white/90" data-testid={`confirm-assign-${u.id}`}>Assign</Button>
                    <Button size="sm" variant="ghost" onClick={() => setAssignFor(null)}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <TeamInviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSent={() => load()} />
    </div>
  );
}
